import { useEffect, useRef, useState } from "react";
import { fixedSlots, keyedEntries } from "@/lib/listIdentity";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion, useAnimationControls, AnimatePresence } from "framer-motion";
import { api, apiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Skull, Users, ArrowLeft, Play, Loader2, Gem, Heart, Trophy, X, Sparkles,
  Shield, Zap, Swords, Hourglass, Flame, Star, Map, Droplet, Target, Wind, Coins, DoorOpen,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { LevelUpCelebration } from "@/components/LevelUpCelebration";
import { ResistGrid } from "@/components/ArenaBits";
import { ActionGrid } from "@/components/CombatActions";
import { ChatPanel } from "@/components/ChatPanel";
import { materialIcon } from "@/lib/materialIcons";
import { CheckCircle2 } from "lucide-react";

function fmtClock(s) {
  const v = Math.max(0, s || 0);
  return `${Math.floor(v / 60)}:${String(v % 60).padStart(2, "0")}`;
}

function HpBar({ hp, max, color, shield, dot }) {
  const pct = Math.max(0, Math.min(100, (hp / Math.max(1, max)) * 100));
  const spct = shield ? Math.max(0, Math.min(100, (shield / Math.max(1, max)) * 100)) : 0;
  const dpct = dot ? Math.min(pct, (dot / Math.max(1, max)) * 100) : 0;
  return (
    <div className="relative h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
      <div className="h-full transition-all duration-300" style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
      {dpct > 0 && (
        <div className="absolute inset-y-0 transition-all duration-300 animate-pulse" data-testid="hp-dot-segment"
          style={{ left: `${pct - dpct}%`, width: `${dpct}%`,
            background: "repeating-linear-gradient(45deg, rgba(217,70,239,0.9) 0 4px, rgba(163,230,53,0.55) 4px 8px)" }} />
      )}
      {spct > 0 && (
        <div className="absolute inset-y-0 left-0 border-r border-sky-300/70 transition-all duration-300"
          style={{ width: `${spct}%`, background: "repeating-linear-gradient(45deg, rgba(56,189,248,0.6) 0 4px, rgba(56,189,248,0.22) 4px 8px)" }} />
      )}
    </div>
  );
}

const LOG_COLOR = {
  telegraph: "text-amber-300 font-medium", cast: "text-rose-400 font-semibold",
  down: "text-red-400 font-medium", regen: "text-emerald-300", defend: "text-emerald-200",
  dodge: "text-cyan-200", miss: "text-white/40", dot: "text-fuchsia-300",
  end: "text-amber-200 font-semibold", stunned: "text-violet-300", interrupt: "text-violet-200 font-medium",
  ability: "text-yellow-200", attack: "text-white/80", start: "text-white/55",
};

// Drives the per-card hit/attack animation off the latest combat log entry.
function useHitFx(cid, fx) {
  const controls = useAnimationControls();
  const [floats, setFloats] = useState([]);
  useEffect(() => {
    if (!fx || !cid) return;
    if (fx.target === cid) {
      controls.start({ x: [0, -10, 10, -6, 6, 0], transition: { duration: 0.42 } });
      if (fx.dmg) setFloats((f) => [...f, { key: fx.key, dmg: fx.dmg, crit: fx.crit }]);
    } else if (fx.attacker === cid) {
      controls.start({ scale: [1, 1.14, 1], y: [0, -5, 0], transition: { duration: 0.32 } });
    }
  }, [fx, cid, controls]);
  const removeFloat = (key) => setFloats((f) => f.filter((x) => x.key !== key));
  return { controls, floats, removeFloat };
}

function FloatLayer({ floats, removeFloat }) {
  return (
    <AnimatePresence>
      {floats.map((fl) => (
        <motion.div key={fl.key} initial={{ opacity: 0, y: 2, scale: 0.7 }}
          animate={{ opacity: [0, 1, 1, 0], y: -30, scale: 1 }} transition={{ duration: 0.85 }}
          onAnimationComplete={() => removeFloat(fl.key)}
          className={`absolute -top-1 left-1/2 -translate-x-1/2 font-mono font-bold pointer-events-none z-20 drop-shadow ${fl.crit ? "text-amber-300 text-base" : "text-rose-300 text-sm"}`}>
          -{fl.dmg}{fl.crit ? "!" : ""}
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

// ── floating "speech" of every combat event, shown above the actor/target ──
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const SAY_COLOR = {
  attack: "#fca5a5", ability: "#fde68a", regen: "#6ee7b7", defend: "#a7f3d0",
  dodge: "#67e8f9", miss: "#cbd5e1", dot: "#f0abfc", stunned: "#c4b5fd",
  interrupt: "#ddd6fe", cast: "#fb7185", telegraph: "#fcd34d", down: "#f87171",
};
function sayFor(e) {
  const moveName = typeof e.move === "string" ? cap(e.move.replace(/_/g, " ")) : null;
  switch (e.type) {
    case "attack": return { cid: e.cid, text: moveName || "Strike", color: SAY_COLOR.attack };
    case "ability": return { cid: e.cid, text: e.crit ? "Special — CRIT!" : "Special!", color: SAY_COLOR.ability };
    case "regen": return { cid: e.cid, text: `+${e.heal || ""} regen`, color: SAY_COLOR.regen };
    case "defend": return { cid: e.cid, text: "Brace!", color: SAY_COLOR.defend };
    case "dodge": return { cid: e.target_cid || e.cid, text: "Dodged!", color: SAY_COLOR.dodge };
    case "miss": return { cid: e.cid, text: "Miss!", color: SAY_COLOR.miss };
    case "dot": return { cid: e.cid, text: `-${e.dmg || ""} 🔥`, color: SAY_COLOR.dot };
    case "stunned": return { cid: e.cid, text: "Stunned!", color: SAY_COLOR.stunned };
    case "interrupt": return { cid: e.cid, text: "Interrupted!", color: SAY_COLOR.interrupt };
    case "cast": return { cid: e.cid, text: `${e.cast || "Cast"}!`, color: SAY_COLOR.cast };
    case "telegraph": return { cid: e.cid, text: e.casting ? `Charging ${e.casting}` : "Charging…", color: SAY_COLOR.telegraph };
    case "down": return { cid: e.cid, text: "Downed!", color: SAY_COLOR.down };
    default: return null;
  }
}
function SayLayer({ says }) {
  return (
    <AnimatePresence>
      {(says || []).map((s) => (
        <motion.div key={s.key} initial={{ opacity: 0, y: 8, scale: 0.8 }}
          animate={{ opacity: [0, 1, 1, 0], y: -36, scale: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 1.5, times: [0, 0.15, 0.7, 1] }}
          className="absolute left-1/2 -translate-x-1/2 -top-4 whitespace-nowrap pointer-events-none z-30 font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-md"
          style={{ color: s.color, background: "rgba(0,0,0,0.72)", border: `1px solid ${s.color}66`, textShadow: `0 0 8px ${s.color}` }}
          data-testid="cavern-say">
          {s.text}
        </motion.div>
      ))}
    </AnimatePresence>
  );
}

function TraitChips({ traits, color = "#a78bfa", max = 4 }) {
  if (!traits || !traits.length) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1.5" data-testid="trait-chips">
      {keyedEntries(traits.slice(0, max), (t) => t.id || t.name).map(({ item: t, key }) => (
        <span key={key} title={t.desc || t.name}
          className="inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 font-mono text-[9px] leading-none max-w-[140px] truncate"
          style={{ borderColor: `${color}55`, background: `${color}14`, color }}>
          <Sparkles className="h-2.5 w-2.5 opacity-80 shrink-0" /><span className="truncate">{t.name}</span>
        </span>
      ))}
      {traits.length > max && <span className="font-mono text-[9px] text-white/40 self-center">+{traits.length - max}</span>}
    </div>
  );
}

function PartyMember({ m, fx, says }) {
  const [open, setOpen] = useState(false);
  const downed = !m.alive;
  const hasIntel = m.atk != null || (m.traits && m.traits.length) || (m.resist && m.resist.length);
  const { controls, floats, removeFloat } = useHitFx(m.cid, fx);
  return (
    <motion.div data-testid={`cavern-member-${m.idx}`} animate={controls}
      className={`relative rounded-xl border transition-colors ${downed ? "border-red-500/30 bg-red-500/[0.06] opacity-60" : m.is_current ? "bg-white/[0.07]" : "border-white/10 bg-white/[0.03]"}`}
      style={{ borderColor: downed ? undefined : m.is_current ? m.color : `${m.color}40`, boxShadow: m.is_current ? `0 0 18px ${m.color}66` : "none" }}>
      <FloatLayer floats={floats} removeFloat={removeFloat} />
      <SayLayer says={says} />
      {m.is_current && !downed && (
        <div className="absolute -top-2 left-3 px-1.5 py-0.5 rounded-full font-mono text-[8px] uppercase tracking-[0.2em] text-black" style={{ background: m.color }}>Acting</div>
      )}
      <div className="p-2.5">
        <div className="flex items-center gap-2.5">
          <div className={`h-12 w-9 rounded overflow-hidden bg-black shrink-0 rarity-${m.rarity} relative`}>
            {m.card?.front_image && <img src={m.card.front_image} alt="" className={`w-full h-full object-cover ${downed ? "grayscale" : ""}`} />}
            {m.stun > 0 && <div className="absolute inset-0 grid place-items-center bg-black/55"><Zap className="h-4 w-4 text-violet-300" /></div>}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-white text-xs truncate flex items-center gap-1">
              {m.is_you && <span className="text-[9px] font-mono bg-violet-400 text-black px-1 rounded">YOU</span>}
              {m.is_ai && <span className="text-[9px] font-mono bg-white/15 text-white/70 px-1 rounded">AI</span>}
              <span className="truncate">{m.card?.title}</span>
            </div>
            <div className="font-mono text-[10px] my-1 flex items-center gap-1.5">
              {downed ? <span className="text-red-400">DOWNED</span> : <span className="text-white/45">{Math.round(m.hp)} / {m.max_hp}</span>}
              {m.defending && !downed && <Shield className="h-3 w-3 text-emerald-300" />}
              {m.dot_total > 0 && !downed && <span className="inline-flex items-center gap-0.5 text-fuchsia-300" data-testid={`cavern-member-dot-${m.idx}`}><Droplet className="h-3 w-3" />{m.dot_total}</span>}
            </div>
            <HpBar hp={m.hp} max={m.max_hp} color={downed ? "#ef4444" : m.color} shield={m.shield} dot={m.dot_total} />
          </div>
        </div>
        {/* compact one-line stat strip + intel toggle */}
        {(m.atk != null) && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 font-mono text-[10px]" data-testid={`cavern-member-stats-${m.idx}`}>
              <span className="inline-flex items-center gap-0.5 text-orange-300"><Swords className="h-3 w-3" />{m.atk}</span>
              <span className="inline-flex items-center gap-0.5 text-cyan-300"><Shield className="h-3 w-3" />{m.def}</span>
              <span className="inline-flex items-center gap-0.5 text-amber-300"><Zap className="h-3 w-3" />{m.spd}</span>
              {m.traits?.length > 0 && <span className="text-violet-300/80 inline-flex items-center gap-0.5"><Sparkles className="h-2.5 w-2.5" />{m.traits.length}</span>}
            </div>
            {hasIntel && (
              <button type="button" onClick={() => setOpen((v) => !v)} data-testid={`cavern-member-intel-${m.idx}`}
                className="inline-flex items-center gap-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-white/40 hover:text-white/80 transition-colors">
                Intel <ChevronDown className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
            )}
          </div>
        )}
        <AnimatePresence initial={false}>
          {open && hasIntel && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden">
              <div className="pt-2 mt-2 border-t border-white/10">
                <TraitChips traits={m.traits} color={m.color} max={6} />
                {m.resist?.length > 0 && <ResistGrid resist={m.resist} hideLabel className="mt-2" />}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

function MonsterPanel({ monster, foe, done, fx, says }) {
  const { controls, floats, removeFloat } = useHitFx(monster.cid, fx);
  return (
    <div className="rounded-xl sm:rounded-3xl p-2.5 sm:p-6 border relative overflow-hidden" style={{ borderColor: monster.is_current ? foe.color : `${foe.color}55`, background: `radial-gradient(120% 120% at 50% 0%, ${foe.color}22, transparent 65%)`, boxShadow: monster.is_current ? `0 0 26px ${foe.color}44` : "none" }}>
      <div className="flex items-center gap-2.5 sm:gap-4">
        <motion.div animate={controls} className={`relative h-10 w-10 sm:h-16 sm:w-16 rounded-lg sm:rounded-2xl flex items-center justify-center shrink-0 ${monster.casting ? "animate-pulse" : ""}`} style={{ background: `${foe.color}1f`, border: `1px solid ${foe.color}66` }}>
          <FloatLayer floats={floats} removeFloat={removeFloat} />
          <SayLayer says={says} />
          <Skull className="h-5 w-5 sm:h-9 sm:w-9" style={{ color: foe.color }} />
          {monster.stun > 0 && <div className="absolute inset-0 grid place-items-center bg-black/55 rounded-lg sm:rounded-2xl"><Zap className="h-4 w-4 sm:h-6 sm:w-6 text-violet-300" /></div>}
        </motion.div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-display text-base sm:text-3xl text-white tracking-[0.04em] truncate">{monster.title || foe.name}</h1>
            <span className="font-mono text-[11px] sm:text-sm flex items-center gap-1.5 sm:gap-2 shrink-0" style={{ color: foe.color }} data-testid="cavern-monster-hp">
              {monster.dot_total > 0 && <span className="inline-flex items-center gap-0.5 text-fuchsia-300 text-[10px] sm:text-xs" data-testid="cavern-monster-dot"><Droplet className="h-3 w-3 sm:h-3.5 sm:w-3.5" />{monster.dot_total}</span>}
              {Math.round(monster.hp)}/{monster.max_hp}
            </span>
          </div>
          <div className="mt-1 sm:mt-2"><HpBar hp={monster.hp} max={monster.max_hp} color={foe.color} dot={monster.dot_total} /></div>
          {(monster.atk != null) && (
            <div className="mt-1 sm:mt-2 flex flex-wrap items-center gap-1 sm:gap-1.5" data-testid="cavern-monster-stats">
              <span className="inline-flex items-center gap-1 rounded bg-black/40 border border-white/10 px-1 sm:px-1.5 py-0.5 font-mono text-[9px] sm:text-[10px] text-orange-300"><Swords className="h-3 w-3" /> {monster.atk}</span>
              <span className="inline-flex items-center gap-1 rounded bg-black/40 border border-white/10 px-1 sm:px-1.5 py-0.5 font-mono text-[9px] sm:text-[10px] text-cyan-300"><Shield className="h-3 w-3" /> {monster.def}</span>
              <span className="inline-flex items-center gap-1 rounded bg-black/40 border border-white/10 px-1 sm:px-1.5 py-0.5 font-mono text-[9px] sm:text-[10px] text-amber-300"><Zap className="h-3 w-3" /> {monster.spd}</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 ml-1 hidden sm:inline">{monster.rarity}</span>
            </div>
          )}
          {/* Traits — secondary info, hidden on mobile so the boss strip
              stays a compact single row. Available on desktop where there's
              room; mobile users still get the resist grid below. */}
          <div className="hidden sm:block">
            <TraitChips traits={monster.traits || foe.traits} color={foe.color} max={6} />
          </div>
        </div>
      </div>
      {/* Resistances — required on mobile too (user request 2026-08-01);
          used to be gated behind sm:block and it hid critical elemental
          info from mobile players. `ResistGrid` is naturally compact. */}
      {monster.resist?.length > 0 && <ResistGrid resist={monster.resist} className="mt-2 sm:mt-4" />}
      {monster.casting && !done && (
        <div data-testid="cavern-monster-cast" className="mt-2 sm:mt-4 rounded-lg sm:rounded-xl border border-amber-400/50 bg-amber-400/10 px-2.5 sm:px-4 py-2 sm:py-2.5 flex items-center gap-2 sm:gap-2.5 animate-pulse">
          <Hourglass className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300 shrink-0" />
          <div className="text-[12px] sm:text-sm leading-snug">
            <span className="font-display tracking-wide text-amber-200">{monster.casting.name}</span>
            <span className="text-white/70"> charging the whole party — </span>
            <span className="text-emerald-300 font-medium">Brace</span><span className="text-white/50"> to halve · </span><span className="text-violet-300 font-medium">Interrupt</span><span className="text-white/50"> to break.</span>
          </div>
        </div>
      )}
    </div>
  );
}

function TurnBar({ bar }) {
  if (!bar?.length) return null;
  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1" data-testid="cavern-turn-bar">
      <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/35 shrink-0 mr-1">Turn order</span>
      {keyedEntries(bar, (b) => b.cid).map(({ item: b, key }, i) => (
        <div key={key}
          className={`shrink-0 flex flex-col items-center gap-0.5 rounded-lg px-1.5 py-1 border transition-all ${b.is_current ? "scale-110" : "opacity-70"}`}
          style={{ borderColor: b.is_current ? b.color : `${b.color}33`, background: b.is_current ? `${b.color}1f` : "transparent" }}>
          <div className="h-7 w-6 rounded overflow-hidden bg-black grid place-items-center" style={{ border: `1px solid ${b.color}55` }}>
            {b.is_monster ? <Skull className="h-3.5 w-3.5" style={{ color: b.color }} />
              : b.front_image ? <img src={b.front_image} alt="" className="w-full h-full object-cover" />
              : <Users className="h-3 w-3 text-white/50" />}
          </div>
          <span className="font-mono text-[7px] uppercase tracking-wider" style={{ color: b.is_current ? b.color : "rgba(255,255,255,0.4)" }}>
            {b.is_you ? "YOU" : b.is_monster ? "FOE" : b.name?.split(" ")[0]?.slice(0, 5)}
          </span>
        </div>
      ))}
    </div>
  );
}

function DungeonProgress({ dungeon }) {
  if (!dungeon) return null;
  const finalTier = dungeon.rooms[dungeon.rooms.length - 1]?.tier;
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-3 mb-4" data-testid="dungeon-progress">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-amber-200 flex items-center gap-1.5"><Map className="h-3.5 w-3.5" /> Dungeon · Room {dungeon.current_room}/{dungeon.total_rooms}</span>
        <span className="font-mono text-[10px] text-amber-200/80 inline-flex items-center gap-1"><Zap className="h-3 w-3" />{dungeon.total_win_xp} XP full clear</span>
      </div>
      <div className="flex items-center gap-1.5">
        {dungeon.rooms.map((r) => (
          <div key={r.room} title={`Room ${r.room} · ${r.name} · tier ${r.tier} · +${r.win_xp} XP`}
            data-testid={`dungeon-pip-${r.room}`}
            className={`flex-1 h-2 rounded-full transition-colors ${r.cleared ? "bg-emerald-400" : r.current ? "bg-amber-300 animate-pulse" : "bg-white/[0.12]"}`} />
        ))}
      </div>
      <div className="flex items-center justify-between mt-1.5 font-mono text-[9px] text-white/40">
        <span>Room 1</span><span>Final · tier {finalTier}</span>
      </div>
    </div>
  );
}

const BUFF_ICON = { heart: Heart, droplet: Droplet, shield: Shield, sparkles: Sparkles, target: Target, wind: Wind, swords: Swords };

function DungeonBuffs({ buffs }) {
  if (!buffs || !buffs.length) return null;
  return (
    <div className="rounded-2xl border border-cyan-400/30 bg-cyan-400/[0.06] p-3 lg:sticky lg:top-4" data-testid="dungeon-buffs">
      <div className="flex items-center gap-1.5 mb-2 font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-200"><Sparkles className="h-3.5 w-3.5" /> Party blessings</div>
      <div className="space-y-1.5">
        {keyedEntries(buffs, (b) => b.key).map(({ item: b, key }, i) => {
          const Icon = BUFF_ICON[b.icon] || Sparkles;
          return (
            <div key={key} data-testid={`dungeon-buff-active-${key}`}
              className="flex items-start gap-2 rounded-lg px-2 py-1.5 bg-white/[0.03] border border-white/10">
              <Icon className="h-4 w-4 text-cyan-300 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="text-white text-xs font-medium">{b.name}</div>
                <div className="text-white/45 text-[10px] leading-snug">{b.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LootSidebar({ dungeon, myUserId }) {
  const loot = dungeon?.loot || [];
  return (
    <div className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.06] p-4 lg:sticky lg:top-4" data-testid="dungeon-loot-sidebar">
      <div className="flex items-center gap-1.5 mb-3 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-200">
        <Coins className="h-3.5 w-3.5" /> Loot so far
      </div>
      <div className="space-y-2">
        {loot.map((l) => (
          <div key={l.user_id} data-testid={`dungeon-loot-${l.user_id}`}
            className={`flex items-center justify-between rounded-lg px-3 py-2 border ${l.user_id === myUserId ? "border-violet-400/40 bg-violet-400/10" : "border-white/10 bg-white/[0.03]"} ${!l.alive ? "opacity-50" : ""}`}>
            <span className="text-white/80 text-xs truncate flex items-center gap-1.5 min-w-0">
              {l.user_id === myUserId && <span className="text-[9px] font-mono bg-violet-400 text-black px-1 rounded shrink-0">YOU</span>}
              <span className="truncate">{l.card_title || l.name}</span>
            </span>
            <span className="font-mono text-[11px] text-amber-200 inline-flex items-center gap-0.5 shrink-0"><Zap className="h-3 w-3" />+{l.xp}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-[10px] text-white/40 leading-snug">Escape now to bank this XP. Push deeper to earn more — but a wipe forfeits it all.</p>
    </div>
  );
}

function IntermissionView({ data, secondsLeft, onVote, voting, onLeave, leaving, onReady, readying, onCampfire, entering, navigate }) {
  const myVote = data.my_vote;
  const voted = !!myVote;
  const youArrived = !!data.you_arrived;
  const youReady = !!data.you_ready;
  const members = data.members || [];
  const sh = data.self_heal || { key: "self_heal", name: "Mend Yourself", desc: "Fully restore your own card.", icon: "heart" };
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="cavern-intermission">
      <Button variant="ghost" size="sm" onClick={() => navigate("/cavern")} className="text-white/50 mb-4"><ArrowLeft className="h-4 w-4 mr-1" /> Back to the mouth</Button>
      {data.dungeon && <DungeonProgress dungeon={data.dungeon} />}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-3xl p-6 border border-amber-400/30 bg-amber-400/[0.05] text-center relative overflow-hidden">
            <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
            <div className="relative">
              <Flame className="h-9 w-9 text-amber-300 mx-auto" />
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-200/80 mt-2">Room {data.room_cleared} of {data.total_rooms} cleared</div>
              <h1 className="font-display text-3xl text-white tracking-[0.04em] mt-1">THE CAMPFIRE</h1>
              <p className="text-white/55 text-sm mt-1 max-w-md mx-auto">Gather at the fire. Take a blessing for the team, mend your own card, or escape and bank your loot. Everyone readies up to push on instantly.</p>
              <div className="mt-3 flex items-center justify-center gap-2">
                <span data-testid="cavern-intermission-timer" className={`inline-block font-mono text-sm px-2.5 py-0.5 rounded ${(secondsLeft ?? 0) <= 15 ? "bg-rose-500/20 text-rose-300" : "bg-white/10 text-white/70"}`}>{fmtClock(secondsLeft)} on the clock</span>
                <span data-testid="cavern-ready-count" className="inline-block font-mono text-sm px-2.5 py-0.5 rounded bg-emerald-400/15 text-emerald-300">{data.ready_count ?? 0}/{data.human_count ?? 0} ready</span>
              </div>
            </div>
          </div>

          {/* Move over to the fire if you're still standing in the cleared room */}
          {!youArrived ? (
            <div className="rounded-2xl border border-amber-400/40 bg-amber-400/[0.06] p-4 text-center" data-testid="cavern-still-in-room">
              <p className="text-white/70 text-sm">You're still in the cleared room. Move over to the campfire to choose your boon.</p>
              <Button data-testid="cavern-to-campfire" disabled={entering} onClick={onCampfire}
                className="mt-3 bg-amber-400 text-black hover:bg-amber-300 font-medium">
                {entering ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Walking over…</> : <><Flame className="h-4 w-4 mr-1.5" /> Move to the Campfire</>}
              </Button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {(data.options || []).map((o) => {
                  const Icon = BUFF_ICON[o.icon] || Sparkles;
                  const chosen = myVote === o.key;
                  return (
                    <button key={o.key} data-testid={`cavern-buff-${o.key}`} disabled={voted || voting}
                      onClick={() => onVote(o.key)}
                      className={`text-left rounded-2xl p-4 border transition-all disabled:cursor-not-allowed ${chosen ? "border-cyan-400 bg-cyan-400/15 ring-1 ring-cyan-400/40" : "border-white/10 bg-white/[0.03] hover:border-cyan-400/50 hover:bg-cyan-400/10"} ${voted && !chosen ? "opacity-40" : ""}`}>
                      <Icon className="h-6 w-6 text-cyan-300 mb-2" />
                      <div className="text-white font-medium text-sm">{o.name}</div>
                      <div className="text-white/50 text-xs mt-1 leading-snug">{o.desc}</div>
                    </button>
                  );
                })}
                {/* Personal full-heal — instead of a team blessing */}
                <button data-testid="cavern-buff-self_heal" disabled={voted || voting}
                  onClick={() => onVote("self_heal")}
                  className={`text-left rounded-2xl p-4 border transition-all disabled:cursor-not-allowed ${myVote === "self_heal" ? "border-rose-400 bg-rose-400/15 ring-1 ring-rose-400/40" : "border-rose-400/20 bg-rose-400/[0.04] hover:border-rose-400/50 hover:bg-rose-400/10"} ${voted && myVote !== "self_heal" ? "opacity-40" : ""}`}>
                  <Heart className="h-6 w-6 text-rose-300 mb-2" />
                  <div className="text-white font-medium text-sm">{sh.name}</div>
                  <div className="text-white/50 text-xs mt-1 leading-snug">{sh.desc}</div>
                </button>
              </div>

              <div className="flex gap-2">
                <Button data-testid="cavern-ready-up" disabled={youReady || readying} onClick={onReady}
                  className={`flex-1 font-medium ${youReady ? "bg-emerald-500/20 text-emerald-300 border border-emerald-400/40" : "bg-emerald-400 text-black hover:bg-emerald-300"}`}>
                  {youReady ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Ready — waiting on the party</> : readying ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Readying…</> : <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Ready up — push on now</>}
                </Button>
                <Button data-testid="cavern-vote-escape" variant="outline" disabled={voted || voting}
                  onClick={() => onVote("escape")}
                  className={`flex-1 border-rose-400/40 text-rose-200 hover:bg-rose-400/10 ${myVote === "escape" ? "bg-rose-400/15 ring-1 ring-rose-400/40" : ""} ${voted && myVote !== "escape" ? "opacity-40" : ""}`}>
                  <DoorOpen className="h-4 w-4 mr-1.5" /> Escape with the loot
                </Button>
              </div>
            </>
          )}

          <button data-testid="cavern-leave-empty" disabled={leaving}
            onClick={onLeave}
            className="w-full text-center text-[11px] font-mono uppercase tracking-[0.2em] text-white/35 hover:text-rose-300 transition-colors disabled:opacity-40 py-1">
            {leaving ? "Slipping away…" : "Leave alone, empty-handed →"}
          </button>
          <p className="text-center text-[10px] text-white/30 -mt-2">You exit the dungeon now and forfeit all loot. The rest of the party fights on.</p>

          {/* Everyone at the fire — and who's still in the room */}
          <div className="rounded-2xl border border-white/10 bg-black/40 p-3" data-testid="cavern-campfire-roster">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/35 mb-2">Around the fire</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {members.map((m, i) => (
                <div key={m.id} data-testid={`campfire-member-${i}`}
                  className={`flex items-center gap-2 rounded-lg border p-1.5 ${m.at_campfire ? "border-amber-400/25 bg-amber-400/[0.05]" : "border-white/10 bg-white/[0.02] opacity-70"}`}>
                  <div className={`relative w-9 aspect-[2.5/3.5] rounded overflow-hidden bg-black shrink-0 rarity-${m.rarity || "common"}`}>
                    {m.card?.front_image && <img src={m.card.front_image} alt={m.card?.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-xs font-medium truncate flex items-center gap-1">
                      {m.is_you && <span className="text-[8px] font-mono bg-violet-400 text-black px-1 rounded">YOU</span>}
                      {m.is_ai && <span className="text-[8px] font-mono bg-white/15 text-white/70 px-1 rounded">AI</span>}
                      <span className="truncate">{m.name}</span>
                    </div>
                    <div className="font-mono text-[9px] text-white/45">HP {m.hp}/{m.max_hp}</div>
                    <div className={`font-mono text-[9px] ${m.at_campfire ? (m.ready ? "text-emerald-300" : "text-amber-300") : "text-white/35"}`}>
                      {m.at_campfire ? (m.ready ? "● ready" : (m.choice ? `chose ${m.choice === "self_heal" ? "to mend" : m.choice === "escape" ? "escape" : "a boon"}` : "at the fire")) : "still in the room…"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1 space-y-4">
          <LootSidebar dungeon={data.dungeon} myUserId={data.my_loot?.user_id} />
          <DungeonBuffs buffs={data.dungeon?.active_buffs} />
          {/* Campfire-only chat */}
          {data.campfire_channel && (
            <div className="rounded-2xl border border-amber-400/20 bg-amber-400/[0.03] p-3" data-testid="cavern-campfire-chat">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-200/70 mb-2 flex items-center gap-1.5"><Flame className="h-3 w-3" /> Campfire chat</div>
              <ChatPanel channel={data.campfire_channel} testidPrefix="campfire-chat" heightPx={240} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const RARITY_HEX = { common: "#9ca3af", uncommon: "#34d399", rare: "#38bdf8", epic: "#a78bfa", legendary: "#fbbf24", goddess: "#f472b6" };

function RewardsBanner({ rewards }) {
  if (!rewards) return null;
  return (
    <div data-testid="cavern-rewards-banner" className="mb-3 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 inline-flex items-center gap-1.5"><Map className="h-3.5 w-3.5" /> Depth {rewards.depth}</span>
      <span data-testid="cavern-card-drop-chance" className="text-xs text-emerald-300">Card {Math.round((rewards.card_chance ?? 0.25) * 100)}% · otherwise credits + Reroll Token</span>
      <span data-testid="cavern-gear-drop-chance" className="text-xs text-amber-300">Gear {Math.round((rewards.gear_chance ?? 0.6) * 100)}%</span>
      <span data-testid="cavern-rarity-odds-label" className="font-mono text-[9px] uppercase tracking-[0.22em] text-white/35">Rarity if a card drops</span>
      <div className="flex flex-wrap items-center gap-1.5" data-testid="cavern-rewards-odds">
        {rewards.odds.map((o) => (
          <span key={o.rarity} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-mono text-[10px]"
            style={{ color: RARITY_HEX[o.rarity] || "#fff", background: `${RARITY_HEX[o.rarity] || "#888"}1f`, border: `1px solid ${RARITY_HEX[o.rarity] || "#888"}55` }}>
            <Gem className="h-2.5 w-2.5" /> {o.rarity} {o.pct}%
          </span>
        ))}
      </div>
      {rewards.mat_tier != null && <span className="font-mono text-[10px] text-cyan-300 inline-flex items-center gap-1"><Coins className="h-3 w-3" /> T{rewards.mat_tier} mats</span>}
      {rewards.sp > 0 && <span className="font-mono text-[10px] text-violet-300 inline-flex items-center gap-1"><Star className="h-3 w-3" /> +{rewards.sp} SP</span>}
    </div>
  );
}


// Keeps a smooth local 1-second-tick countdown that resyncs whenever the
// server pushes a fresh value (SSE ping or the safety-net poll), instead of
// showing whatever stale number happened to arrive with the last update —
// the previous behaviour only stepped down on a refetch, so it jumped by
// however many seconds had elapsed since the last SSE event (10s, 7s, 15s...).
function useServerCountdown(serverValue, active) {
  const [local, setLocal] = useState(serverValue ?? null);
  const lastServer = useRef(serverValue);
  useEffect(() => {
    if (serverValue !== lastServer.current) {
      lastServer.current = serverValue;
      setLocal(serverValue ?? null);
    }
  }, [serverValue]);
  useEffect(() => {
    if (!active || local == null || local <= 0) return;
    const t = setTimeout(() => setLocal((s) => (s == null ? null : Math.max(0, s - 1))), 1000);
    return () => clearTimeout(t);
  }, [local, active]);
  return local;
}

export default function CavernRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [timer, setTimer] = useState(null);
  const lastTurnKey = useRef(null);
  const fxKey = useRef(-1);
  const [fx, setFx] = useState(null);
  const [says, setSays] = useState([]);
  const sayKey = useRef(null);

  const { data } = useQuery({
    queryKey: ["cavern-delve", id],
    queryFn: async () => (await api.get(`/cavern/${id}`)).data,
    // The delve now advances server-side on its own heartbeat (AI/monster
    // turns, intermission-vote countdowns) even with no tab open, and the SSE
    // stream below pushes a live refetch the instant anything changes — this
    // interval is just a slow safety net in case a ping is missed.
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "open" || s === "fighting" || s === "intermission" ? 6000 : false;
    },
  });

  // Live delve state — an SSE ping fires the instant the server ticks this
  // delve (a turn resolves, a vote lands, someone joins), so we refetch once
  // immediately instead of relying purely on the slow safety-net poll above.
  useEffect(() => {
    if (!id) return;
    const es = new EventSource(`${API}/cavern/${id}/stream`, { withCredentials: true });
    es.onerror = () => { api.get("/auth/me").catch(() => es.close()); };
    es.onmessage = () => { qc.invalidateQueries({ queryKey: ["cavern-delve", id] }); };
    return () => es.close();
  }, [id, qc]);

  // Trigger an attack/hit animation off the newest damaging log entry.
  useEffect(() => {
    const lg = data?.log || [];
    for (let k = lg.length - 1; k >= 0; k--) {
      const e = lg[k];
      if (e.target_cid && e.dmg && (e.type === "attack" || e.type === "ability")) {
        if (e.i > fxKey.current) {
          fxKey.current = e.i;
          setFx({ key: e.i, attacker: e.cid, target: e.target_cid, dmg: e.dmg, crit: e.crit });
        }
        break;
      }
    }
  }, [data?.log]);

  // Float EVERY combat event as short "speech" above the actor/target.
  useEffect(() => {
    const lg = data?.log || [];
    if (!lg.length) return;
    if (sayKey.current === null) { sayKey.current = lg[lg.length - 1].i; return; } // skip backlog
    const fresh = lg.filter((e) => e.i > sayKey.current);
    if (!fresh.length) return;
    sayKey.current = lg[lg.length - 1].i;
    const adds = [];
    for (const e of fresh) {
      const s = sayFor(e);
      if (s && s.cid) adds.push({ key: e.i, ...s });
    }
    if (!adds.length) return;
    setSays((p) => [...p, ...adds]);
    adds.forEach((a) => setTimeout(() => setSays((p) => p.filter((x) => x.key !== a.key)), 1600));
  }, [data?.log]);

  // Smooth local countdown for the active player's turn timer.
  useEffect(() => {
    if (data?.status !== "fighting" || !data.is_your_turn) { setTimer(null); return; }
    const key = `${data.current}-${data.actions}`;
    if (lastTurnKey.current !== key) {
      lastTurnKey.current = key;
      setTimer(data.turn_seconds_left ?? null);
    }
  }, [data]);

  useEffect(() => {
    if (timer == null || timer <= 0) return;
    const t = setTimeout(() => setTimer((s) => (s == null ? null : Math.max(0, s - 1))), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  // Intermission (campfire) and lobby (waiting-for-players) countdowns — same
  // smooth-ticking treatment as the turn timer above.
  const introSeconds = useServerCountdown(data?.seconds_left, data?.status === "intermission");
  const lobbySeconds = useServerCountdown(data?.seconds_open_left, data?.status === "open");

  const start = useMutation({
    mutationFn: async () => (await api.post(`/cavern/${id}/start`)).data,
    onSuccess: (d) => qc.setQueryData(["cavern-delve", id], d),
    onError: (e) => toast.error(apiError(e, "Couldn't begin the delve")),
  });
  const leave = useMutation({
    mutationFn: async () => (await api.post(`/cavern/${id}/leave`)).data,
    onSuccess: () => { toast.success("Left the delve"); navigate("/cavern"); },
    onError: (e) => toast.error(apiError(e, "Couldn't leave")),
  });
  const act = useMutation({
    mutationFn: async (body) => (await api.post(`/cavern/${id}/action`, body)).data,
    onSuccess: (d) => qc.setQueryData(["cavern-delve", id], d),
    onError: (e) => toast.error(apiError(e, "Couldn't act")),
  });
  const vote = useMutation({
    mutationFn: async (choice) => (await api.post(`/cavern/${id}/vote`, { choice })).data,
    onSuccess: (d) => qc.setQueryData(["cavern-delve", id], d),
    onError: (e) => toast.error(apiError(e, "Couldn't cast your vote")),
  });
  const ready = useMutation({
    mutationFn: async () => (await api.post(`/cavern/${id}/ready`)).data,
    onSuccess: (d) => qc.setQueryData(["cavern-delve", id], d),
    onError: (e) => toast.error(apiError(e, "Couldn't ready up")),
  });
  const leaveDungeon = useMutation({
    mutationFn: async () => (await api.post(`/cavern/${id}/intermission_leave`)).data,
    onSuccess: () => { toast.success("You slip out of the dungeon empty-handed."); navigate("/cavern"); },
    onError: (e) => toast.error(apiError(e, "Couldn't leave the run")),
  });
  const campfire = useMutation({
    mutationFn: async () => (await api.post(`/cavern/${id}/campfire`)).data,
    onSuccess: (d) => qc.setQueryData(["cavern-delve", id], d),
    onError: (e) => toast.error(apiError(e, "Couldn't reach the campfire")),
  });

  if (!data) return <div className="flex items-center justify-center min-h-[60vh] text-white/50"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Entering the cavern…</div>;

  const foe = data.foe;

  // ── LOBBY ────────────────────────────────────────────────────────────────
  if (data.status === "open") {
    const seats = fixedSlots(data.slots);
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
        <Button variant="ghost" size="sm" onClick={() => navigate("/cavern")} className="text-white/50 mb-6"><ArrowLeft className="h-4 w-4 mr-1" /> Back to the mouth</Button>
        <div className="rounded-3xl p-8 border relative overflow-hidden" style={{ borderColor: `${foe.color}44`, background: `radial-gradient(120% 120% at 50% 0%, ${foe.color}1a, transparent 60%)` }}>
          <div className="text-center">
            <div className="inline-flex items-center justify-center h-16 w-16 rounded-2xl mb-3" style={{ background: `${foe.color}1f`, border: `1px solid ${foe.color}55` }}>
              <Skull className="h-8 w-8" style={{ color: foe.color }} />
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">Depth {foe.depth} · {foe.reward} drop</div>
            <h1 className="font-display text-4xl text-white tracking-[0.04em] mt-1">{foe.name}</h1>
            <p className="text-white/50 text-sm mt-1 max-w-md mx-auto">{foe.blurb}</p>
            {foe.win_xp != null && (
              <div className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-amber-400/10 border border-amber-400/30 px-2 py-1" data-testid="cavern-lobby-win-xp" title="XP each surviving delver earns on a win — scales with the colour (strength) of this monster's traits">
                <Zap className="h-3.5 w-3.5 text-amber-300" />
                <span className="font-mono text-[11px] text-amber-200">+{foe.win_xp} XP / delver on win</span>
              </div>
            )}
          </div>

          {data.dungeon && <div className="mt-6"><DungeonProgress dungeon={data.dungeon} /></div>}

          <div className="mt-8 flex items-center justify-between">
            <h3 className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/45 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> The party · {data.party.length}/{data.slots}</h3>
            <span className="font-mono text-[11px] text-violet-300" data-testid="cavern-lobby-timer">{lobbySeconds}s</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mt-3">
            {seats.map(({ id: slotId, index: i }) => {
              const m = data.party[i];
              if (!m) return (
                <div key={slotId} className="rounded-xl p-3 border border-dashed border-white/10 text-white/30 text-xs flex items-center justify-center min-h-[68px]" data-testid={`cavern-seat-empty-${i}`}>
                  Empty seat — AI ally fills on descent
                </div>
              );
              return (
                <div key={slotId} data-testid={`cavern-seat-${i}`} className="rounded-xl p-2.5 border bg-white/[0.03] flex items-center gap-2.5" style={{ borderColor: `${m.color}44` }}>
                  <div className={`h-12 w-9 rounded overflow-hidden bg-black shrink-0 rarity-${m.rarity}`}>{m.card?.front_image && <img src={m.card.front_image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="min-w-0">
                    <div className="text-white text-sm truncate flex items-center gap-1">{m.is_you && <span className="text-[9px] font-mono bg-violet-400 text-black px-1 rounded">YOU</span>}{m.name}</div>
                    <div className="font-mono text-[10px] text-white/45 truncate">{m.card?.title}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center justify-center gap-3">
            {data.is_host ? (
              <>
                <Button data-testid="cavern-start-button" disabled={!data.can_start || start.isPending} onClick={() => start.mutate()} className="bg-violet-300 text-black hover:bg-violet-200 disabled:opacity-40 font-medium px-6">
                  {start.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Descending…</> : <><Play className="h-4 w-4 mr-1.5" /> {data.can_start ? "Begin the Descent" : "Setting the floor…"}</>}
                </Button>
                <Button data-testid="cavern-cancel-button" variant="outline" disabled={leave.isPending} onClick={() => leave.mutate()} className="border-white/15 text-white/70"><X className="h-4 w-4 mr-1" /> Cancel</Button>
              </>
            ) : (
              <>
                <div className="text-white/50 text-sm flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Waiting for the host to begin…</div>
                <Button data-testid="cavern-leave-button" variant="outline" disabled={leave.isPending} onClick={() => leave.mutate()} className="border-white/15 text-white/70">Leave</Button>
              </>
            )}
          </div>
          {data.is_host && !data.can_start && <p className="text-center text-[11px] text-white/35 mt-2">You can begin shortly — let allies join the party.</p>}
        </div>
      </div>
    );
  }

  // ── DUNGEON INTERMISSION ───────────────────────────────────────────────────
  if (data.status === "intermission") {
    return <IntermissionView data={data} secondsLeft={introSeconds} onVote={(c) => vote.mutate(c)} voting={vote.isPending} onLeave={() => leaveDungeon.mutate()} leaving={leaveDungeon.isPending} onReady={() => ready.mutate()} readying={ready.isPending} onCampfire={() => campfire.mutate()} entering={campfire.isPending} navigate={navigate} />;
  }

  // ── INTERACTIVE TURN-BASED COMBAT ──────────────────────────────────────────
  const monster = data.monster || {};
  const log = data.log || [];
  const yt = data.your_turn;
  const isYourTurn = !!data.is_your_turn && !!yt;
  const done = data.status === "done";

  return (
    <div className="mx-auto max-w-[1600px] px-3 sm:px-5 py-6">
      <LevelUpCelebration levelUps={done ? data.my_level_ups : null} />
      <Button variant="ghost" size="sm" onClick={() => navigate("/cavern")} className="text-white/50 mb-3"><ArrowLeft className="h-4 w-4 mr-1" /> Back to the mouth</Button>

      {data.dungeon && <DungeonProgress dungeon={data.dungeon} />}

      {/* 4/5 main battle stage + 1/5 allies rail */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 items-start">
        <div className="lg:col-span-4 min-w-0 space-y-2 sm:space-y-3" data-testid="cavern-stage">
          {/* Boss strip (mobile) / full panel (desktop) — kept above the fighter
              stage. On phone the panel collapses to a compact horizontal bar so
              the fighter action grid below has full width to work with. */}
          <MonsterPanel monster={monster} foe={foe} done={done} fx={fx} says={says.filter((s) => s.cid === monster.cid)} />
          {!done && <TurnBar bar={data.turn_bar} />}
          <RewardsBanner rewards={data.rewards} />

          {!done && (
            <ActiveFighterStage
              party={data.party}
              yt={yt}
              isYourTurn={isYourTurn}
              act={act}
              timer={timer}
              fx={fx}
              says={says}
            />
          )}

          {/* Battle log — compact on mobile so it doesn't push the action
              grid off-screen. Only the last 5 events are shown at the mobile
              breakpoint; desktop keeps the roomier 14-event window. */}
          <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-black/40 p-2.5 sm:p-4 max-h-24 sm:min-h-[100px] sm:max-h-56 overflow-y-auto" data-testid="cavern-battle-log">
            <div className="space-y-1 sm:space-y-1.5">
              {log.slice(-14).map((e) => (
                <div key={e.i} className="text-[12px] sm:text-sm">
                  <span className="font-mono text-[9px] sm:text-[10px] text-white/25 mr-1.5 sm:mr-2">#{e.round}</span>
                  <span className={LOG_COLOR[e.type] || "text-white/60"}>{e.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right rail — allies + dungeon buffs */}
        <aside className="lg:col-span-1 space-y-3" data-testid="cavern-allies-rail">
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" /> Allies
              </h3>
              <span className="font-mono text-[10px] text-white/45">{data.party.filter((m) => m.alive).length}/{data.party.length}</span>
            </div>
            <div className="space-y-2">
              {data.party.map((m) => <PartyMember key={m.cid} m={m} fx={fx} says={says.filter((s) => s.cid === m.cid)} />)}
            </div>
          </div>
          {data.dungeon?.active_buffs?.length > 0 && <DungeonBuffs buffs={data.dungeon.active_buffs} />}
        </aside>
      </div>

      {/* Room cleared — campfire gate (more rooms remain) */}
      {done && data.awaiting_campfire && (
        <div data-testid="cavern-room-cleared" className="mt-6 rounded-3xl p-6 border border-amber-400/40 bg-amber-400/[0.06] text-center relative overflow-hidden">
          <div className="absolute inset-0 holo-scan opacity-10 pointer-events-none" />
          <div className="relative">
            <Flame className="h-10 w-10 text-amber-300 mx-auto" />
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-200/80 mt-2">
              Room {data.dungeon?.current_room ?? "?"} of {data.dungeon?.total_rooms ?? "?"} cleared
            </div>
            <h2 className="font-display text-3xl text-white tracking-[0.04em] mt-1">ROOM CLEARED</h2>
            <p className="text-white/55 text-sm mt-1 max-w-md mx-auto">The dust settles. Leave the room and gather your loot at the campfire before deciding whether to press deeper.</p>
            <Button data-testid="cavern-to-campfire" disabled={campfire.isPending} onClick={() => campfire.mutate()}
              className="mt-5 bg-amber-400 text-black hover:bg-amber-300 font-medium px-6">
              {campfire.isPending ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Leaving the room…</> : <><Flame className="h-4 w-4 mr-1.5" /> Head to the Campfire</>}
            </Button>
          </div>
        </div>
      )}

      {/* Results */}
      {done && data.results && <CavernResults data={data} foe={foe} navigate={navigate} />}
    </div>
  );
}


import { RTEXT as RARITY_TEXT } from "@/lib/inventoryVisuals";
import { InventoryIcon, InventoryName } from "@/components/InventoryIdentity";

// The bottom half of the battle stage: the current turn's card (yours if it's
// your turn, otherwise whichever party card is 'is_current'), with the action
// grid tucked to its right. When neither is available (monster's turn or
// no data), it collapses to a subtle 'waiting' indicator so the boss panel
// above still has vertical dominance.
function ActiveFighterStage({ party, yt, isYourTurn, act, timer, fx, says }) {
  // Prefer your own card when it's your turn; else the AI ally that's acting;
  // else the first alive party member as a passive display.
  const you = (party || []).find((m) => m.is_you);
  const currentAi = (party || []).find((m) => !m.is_you && m.is_current);
  const anyAlive = (party || []).find((m) => m.alive);
  const active = isYourTurn ? you : (currentAi || you || anyAlive);

  if (!active) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/30 p-6 min-h-[220px] flex items-center justify-center text-white/40 text-sm" data-testid="cavern-stage-idle">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Awaiting the delvers…
      </div>
    );
  }

  return (
    <div className={`rounded-2xl border ${isYourTurn ? "border-violet-400/40 bg-violet-400/[0.05]" : "border-white/10 bg-white/[0.02]"} p-3 sm:p-4`} data-testid="cavern-active-stage">
      {isYourTurn && (
        <div className="flex items-center justify-between mb-3">
          <span className="font-display tracking-[0.06em] text-violet-200 text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4" /> Your turn
          </span>
          <span data-testid="cavern-turn-timer" className={`font-mono text-xs px-2 py-0.5 rounded ${(timer ?? 0) <= 6 ? "bg-rose-500/20 text-rose-300" : "bg-white/10 text-white/70"}`}>
            {Math.max(0, timer ?? 0)}s
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[minmax(220px,280px)_1fr] gap-3 items-start">
        {/* Active fighter — you-in-the-fight */}
        <div data-testid="cavern-active-fighter">
          <PartyMember m={active} fx={fx} says={says.filter((s) => s.cid === active.cid)} />
        </div>

        {/* Action panel — attack / defend / heal / interrupt */}
        {isYourTurn ? (
          <div className="rounded-xl border border-white/10 bg-black/30 p-2.5">
            <ActionGrid
              testidPrefix="cavern-action"
              disabled={act.isPending}
              pending={act.isPending}
              moves={yt.moves || []}
              abilities={yt.abilities || []}
              interrupt={yt.stun ? { current_cd: yt.stun.current_cd || 0 } : null}
              allies={(party || []).map((m) => ({ key: m.cid, label: m.name, hp: Math.round(m.hp), maxHp: m.max_hp, alive: m.alive }))}
              onAttack={(mid, cid) => act.mutate(cid != null ? { action: "attack", move_id: mid, target_cid: cid } : { action: "attack", move_id: mid })}
              onAbility={(aid) => act.mutate({ action: "ability", ability_id: aid })}
              onDefend={() => act.mutate({ action: "defend" })}
              onInterrupt={() => act.mutate({ action: "stun" })}
            />
          </div>
        ) : (
          <div className="rounded-xl border border-white/10 bg-black/25 p-6 flex items-center justify-center text-white/45 text-sm min-h-[140px]" data-testid="cavern-waiting">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> The battle unfolds…
          </div>
        )}
      </div>
    </div>
  );
}

function CardTile({ card, bonus, onClick }) {
  return (
    <button onClick={onClick} title={`${card?.title} · ${card?.rarity}${bonus ? " (bonus)" : ""}`}
      className={`relative h-24 w-[68px] shrink-0 rounded-lg overflow-hidden bg-black rarity-${card?.rarity} transition-transform hover:scale-105`}>
      {card?.front_image && <img src={card.front_image} alt={card?.title} className="absolute inset-0 w-full h-full object-cover" draggable={false} />}
      <div className="foil-overlay" />
      {bonus && <span className="absolute top-0.5 left-0.5 font-mono text-[7px] uppercase tracking-wider rounded bg-amber-300/90 text-black px-1">bonus</span>}
      <div className="absolute inset-x-0 bottom-0 bg-black/75 px-1 py-0.5"><div className="font-mono text-[7px] text-white truncate">{card?.title}</div></div>
    </button>
  );
}

// Aggregates each delver's outcomes (mint + bonus cards, gear, XP, or a forfeit)
// into one clear per-player summary so the result screen shows the TRUE rewards.
function CavernResults({ data, foe, navigate }) {
  const res = data.results;
  const rfoe = res.foe || foe || {};
  const won = res.won;
  const myId = data.my_outcome?.user_id;

  const byUser = {};
  for (const o of res.outcomes || []) {
    const u = byUser[o.user_id] || (byUser[o.user_id] = { user_id: o.user_id, name: o.name, cards: [], gear: [], tokens: [], mats: [], credits: 0, sp: 0, xp: 0, lost: false, lostCard: null });
    if (o.outcome === "mint" || o.outcome === "bonus") u.cards.push({ ...o.card, bonus: o.outcome === "bonus" });
    else if (o.outcome === "gear" && o.gear) u.gear.push(o.gear);
    else if (o.outcome === "token" && o.token) u.tokens.push(o.token);
    else if (o.outcome === "material" && o.material) u.mats.push(o.material);
    else if (o.outcome === "sp") u.sp += (o.sp || 0);
    else if (o.outcome === "credits") u.credits += (o.credits || 0);
    else if (o.outcome === "forfeit") { u.lost = true; u.lostCard = o.card; }
    if (o.xp != null) u.xp = Math.max(u.xp, o.xp);
  }
  const players = Object.values(byUser);
  const me = byUser[myId];
  const partyCards = players.reduce((n, p) => n + p.cards.length, 0);
  const partyXp = players.reduce((n, p) => n + (p.xp || 0), 0);

  return (
    <div data-testid="cavern-results" className="mt-6 rounded-3xl p-6 border overflow-hidden relative"
      style={{ borderColor: won ? "#34d39955" : "#ef444455", background: won ? "#34d3990f" : "#ef44440f" }}>
      {/* Banner */}
      <div className="text-center mb-5">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 240, damping: 18 }}>
          {won ? <Trophy className="h-10 w-10 text-emerald-300 mx-auto" /> : <Skull className="h-10 w-10 text-red-400 mx-auto" />}
        </motion.div>
        <h2 className={`font-display text-3xl tracking-[0.06em] mt-2 ${won ? "text-emerald-300" : "text-red-400"}`}>{won ? "THE CAVERN FALLS" : "THE PARTY WIPES"}</h2>
        <p className="text-white/55 text-sm mt-1">
          {won ? "You bested " : "Slain by "}<span className="font-medium text-white/80">{rfoe.name || "the depths"}</span>
        </p>
        <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
          {res.mode === "dungeon" && (
            <span data-testid="dungeon-rooms-cleared" className="font-mono text-[10px] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 bg-amber-400/10 border border-amber-400/30 text-amber-200 inline-flex items-center gap-1">
              <Map className="h-3 w-3" /> {res.rooms_cleared} / {data.dungeon?.total_rooms ?? res.rooms_cleared} rooms
            </span>
          )}
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 bg-violet-400/10 border border-violet-400/30 text-violet-200 inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> {partyCards} card{partyCards === 1 ? "" : "s"} won
          </span>
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] rounded-full px-2.5 py-1 bg-amber-400/10 border border-amber-400/30 text-amber-200 inline-flex items-center gap-1">
            <Zap className="h-3 w-3" /> {partyXp} party XP
          </span>
        </div>
      </div>

      {/* Your spoils */}
      {me && (
        <div className="rounded-2xl p-4 mb-4 border" data-testid="cavern-my-outcome"
          style={{ borderColor: me.lost && me.cards.length === 0 ? "#ef444455" : "#34d39955", background: me.lost && me.cards.length === 0 ? "#ef444414" : "#34d39914" }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2 text-center">Your spoils</div>
          {me.cards.length > 0 ? (
            <>
              <div className="inline-flex items-center gap-1.5 text-emerald-300 font-medium justify-center w-full"><Sparkles className="h-4 w-4" /> You claimed {me.cards.length} card{me.cards.length === 1 ? "" : "s"}!</div>
              <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
                {me.cards.map((c, i) => <CardTile key={c.id || i} card={c} bonus={c.bonus} onClick={() => navigate(`/card/${c.id}`)} />)}
              </div>
            </>
          ) : me.lost ? (
            <div data-testid="cavern-card-loss-result" className="text-red-300 text-center text-sm"><Skull className="h-4 w-4 inline mr-1" /> Your <span className="font-medium">{me.lostCard?.title}</span> {me.lostCard?.locked_out ? "remains in your Playden, permanently locked out." : "fell and was forfeited to the house."}</div>
          ) : null}
          <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
            {me.credits > 0 && <span data-testid="cavern-my-credits" className="text-sm text-amber-200 inline-flex items-center gap-1"><Coins className="h-4 w-4" /> +{me.credits.toLocaleString()} credits</span>}
            {me.gear.map((g, i) => (
              <span key={g.id || i} data-testid={`cavern-my-gear-${i}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono border border-white/15 bg-white/[0.04] ${RARITY_TEXT[g.rarity] || "text-white/70"}`}>
                <InventoryIcon item={g} className="h-3 w-3" /><InventoryName item={g} className="text-[11px]" />{g.slot ? ` · ${g.slot}` : ""}
              </span>
            ))}
            {me.xp > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-amber-400/10 border border-amber-400/30 text-amber-200 font-mono text-[11px]" data-testid="cavern-my-xp">
                <Zap className="h-3 w-3" /> +{me.xp} XP earned
              </span>
            )}
            {me.sp > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 bg-fuchsia-400/10 border border-fuchsia-400/30 text-fuchsia-200 font-mono text-[11px]" data-testid="cavern-my-sp">
                <Sparkles className="h-3 w-3" /> +{me.sp} SP
              </span>
            )}
            {keyedEntries(me.mats, (m) => `${m.mat_key}:${m.tier}`).map(({ item: m, key }, i) => {
              const MIcon = materialIcon(m.mat_key);
              return (
                <span key={key} data-testid={`cavern-my-material-${i}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono border border-emerald-400/30 bg-emerald-400/10 text-emerald-200">
                  <MIcon className="h-3 w-3" /> {m.qty}× T{m.tier} {m.mat_name}
                </span>
              );
            })}
            {keyedEntries(me.tokens, (t) => t.id || t.name).map(({ item: t, key }, i) => (
              <span key={key} data-testid={`cavern-my-token-${i}`} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-mono border border-violet-400/30 bg-violet-400/10 text-violet-200">
                <Coins className="h-3 w-3" /> {t.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Party breakdown */}
      <div className="space-y-1.5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-1">Party</div>
        {players.map((p) => (
          <div key={p.user_id} data-testid={`cavern-party-${p.user_id}`} className="flex flex-wrap items-center justify-between gap-2 text-sm rounded-lg px-3 py-2 bg-white/[0.03]">
            <span className="text-white/80 flex items-center gap-1.5 min-w-0 truncate">
              {p.user_id === myId ? <Star className="h-3 w-3 text-amber-300 shrink-0" /> : <Heart className="h-3 w-3 text-white/30 shrink-0" />}
              <span className="truncate">{p.name}</span>
            </span>
            <span className="flex max-w-full items-center gap-2 flex-wrap justify-end">
              {p.credits > 0 && <span data-testid={`cavern-party-credits-${p.user_id}`} className="text-xs text-amber-200">+{p.credits.toLocaleString()}c</span>}
              {p.xp > 0 && <span className="font-mono text-[11px] text-amber-200 inline-flex items-center gap-0.5"><Zap className="h-3 w-3" />+{p.xp}</span>}
              {p.gear.map((g, i) => <span key={g.id || i} className="inline-flex items-center gap-0.5"><InventoryIcon item={g} className="h-2.5 w-2.5" /><InventoryName item={g} className="text-[10px]" /></span>)}
              {p.cards.length > 0 ? (
                <span className="font-mono text-[11px] text-emerald-300 inline-flex items-center gap-0.5"><Sparkles className="h-3 w-3" />{p.cards.length} card{p.cards.length === 1 ? "" : "s"}</span>
              ) : p.lost ? (
                <span className="font-mono text-[11px] text-red-400">{p.lostCard?.locked_out ? "card locked out" : "card forfeited"}</span>
              ) : null}
            </span>
          </div>
        ))}
      </div>

      <div className="flex justify-center mt-5">
        <Button data-testid="cavern-again-button" onClick={() => navigate("/cavern")} className="bg-violet-300 text-black hover:bg-violet-200 px-6">Delve again</Button>
      </div>
    </div>
  );
}
